import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { AgentComparison, CompareAgentService, ShortlistService, SortByType } from '@rma/public/compare-agent/private-api/da-compare-agent';
import { PlatformService } from '@rma/public/generic/util/environment';
import { LocationDetails } from '@rma/public/locations/public/api-location-details';
import { LocationInfo } from '@rma/public/locations/public/pills';
import { catchError, EMPTY, exhaustMap, filter, map, of, pipe, switchMap, take, tap } from 'rxjs';
import {
  extractUniquePropertyTypes,
  getAchievements,
  getAgentPropertyTypes,
  getLanguages,
  getSentiments,
  getSoldStats,
  hasBestStats,
} from '../util/mappers';

export interface CompareAgentState {
  sortBy: SortByType;
  propertyType: string | null;

  agentsLoading: boolean;
  agents: AgentComparison[];
  agentsError: string | undefined;

  marketsLoading: boolean;
  markets: LocationInfo[];
  marketsError: string | undefined;

  location: LocationDetails | undefined;
}

const initialState: CompareAgentState = {
  sortBy: 'TotalReviews',
  propertyType: null,
  agentsLoading: true,
  agents: [],
  agentsError: undefined,
  marketsLoading: true,
  markets: [],
  marketsError: undefined,
  location: undefined,
};

const getError = (error: HttpErrorResponse): string => {
  console.error(error);

  return error.error.message ?? error.message ?? '';
};

export const CompareAgentStore = signalStore(
  withState(initialState),
  withComputed(({ agents, propertyType }) => {
    const propertyTypes = computed(() => extractUniquePropertyTypes(agents()));

    return {
      propertyTypes,
      effectivePropertyType: computed(() => {
        const selected = propertyType();

        return selected !== null && propertyTypes().includes(selected) ? selected : null;
      }),
    };
  }),
  withComputed((store) => {
    // isAuthenticated$ stays silent until the first user load completes, so undefined means "not resolved yet":
    // gate the content, but hold the login card back so it never flashes at a user who turns out to be signed in.
    const isAuthenticated = toSignal(inject(PublicUserContextService).isAuthenticated$);

    return {
      gated: computed(() => isAuthenticated() !== true && !store.location()),
      showLoginCard: computed(() => isAuthenticated() === false && !store.location()),
    };
  }),
  withMethods((store) => {
    const platformService = inject(PlatformService);
    const shortlistService = inject(ShortlistService);
    const compareAgentService = inject(CompareAgentService);

    const loadAgents = rxMethod<{ agentCodes: string[]; sortBy: SortByType; resolved: boolean }>(
      pipe(
        // An unresolved shortlist is not an empty one: answering it would settle `agentsLoading` and
        // flash the empty state over agents still on their way.
        filter(({ resolved }) => resolved),
        tap(() => patchState(store, (state) => ({ agentsLoading: state.agents.length === 0 }))),
        switchMap(({ agentCodes, sortBy }) =>
          (!agentCodes.length
            ? of([])
            : compareAgentService.agents(agentCodes, sortBy).pipe(
                map(({ results }) => {
                  const propertyTypes = extractUniquePropertyTypes(results);
                  return results.map((r) => ({
                    ...r,
                    agentProfileUrl: r.agentProfileUrl ? `/${r.agentProfileUrl}` : r.agentProfileUrl,
                    agencyProfileUrl: r.agencyProfileUrl ? `/${r.agencyProfileUrl}` : r.agencyProfileUrl,
                    agentProfileReviewsUrl: r.agentProfileReviewsUrl ? `/${r.agentProfileReviewsUrl}` : r.agentProfileReviewsUrl,
                    agentProfilePropertiesUrl: r.agentProfilePropertiesUrl
                      ? `/${r.agentProfilePropertiesUrl}`
                      : r.agentProfilePropertiesUrl,
                    bestTotalReviews: hasBestStats(r.bestStatistics, 'TotalReviews'),
                    sales: getSoldStats(r.statistics, r.bestStatistics),
                    propertyTypes: getAgentPropertyTypes(r.statistics, propertyTypes),
                    languages: getLanguages(r),
                    sentiments: getSentiments(r),
                    achievements: getAchievements(r),
                  }));
                }),
              )
          ).pipe(
            tapResponse({
              next: (agents) => patchState(store, { agents, agentsLoading: false, agentsError: undefined }),
              error: (error: HttpErrorResponse) => patchState(store, { agentsError: getError(error), agentsLoading: false }),
            }),
          ),
        ),
      ),
    );

    const loadMarkets = rxMethod<string[]>(
      pipe(
        tap(() => patchState(store, (state) => ({ marketsLoading: state.markets.length === 0 }))),
        switchMap((agentCodes) =>
          (!agentCodes.length
            ? of([])
            : compareAgentService.markets(agentCodes).pipe(
                map(({ results }) => results),
                map((markets) =>
                  markets.map((m) => ({
                    id: m.locationId,
                    locationType: m.locationType,
                    name: m.name,
                    shortName: m.displayName,
                    url: m.locationProfileUrl,
                  })),
                ),
              )
          ).pipe(
            tapResponse({
              next: (markets) => patchState(store, { markets, marketsLoading: false, marketsError: undefined }),
              error: (error: HttpErrorResponse) => patchState(store, { marketsError: getError(error), marketsLoading: false }),
            }),
          ),
        ),
      ),
    );

    // Sign-in lands on whatever page the user left, so the merge question waits until they are here.
    const settlePendingMerge = rxMethod<boolean>(
      pipe(
        filter(Boolean),
        exhaustMap(() => shortlistService.settlePendingMerge().pipe(catchAndLogError(EMPTY))),
      ),
    );

    // The shortlist is a signal now, so the effects read it (and store state) through
    // computed inputs — rxMethod re-runs whenever either changes, no observable bridge needed.
    const loadEffects = () => {
      settlePendingMerge(shortlistService.hasPendingMerge);

      // Track the loaded shortlist and the sort only. `remove` filters `agents` optimistically and
      // the shortlist signal then shrinks to match; tracking that echo would refetch a list the user
      // already sees. The codes themselves are still read live, so a sort after a remove doesn't ask
      // the server to re-sort an agent we just dropped.
      loadAgents(
        computed(() => {
          shortlistService.loadedShortlist();
          const sortBy = store.sortBy();
          // Tracked in its own right: a load that settles on the same codes — an empty list that
          // stays empty — moves nothing else this effect reads.
          const resolved = shortlistService.shortlistResolved();

          return untracked(() => ({ agentCodes: shortlistService.shortlist(), sortBy, resolved }));
        }),
      );

      // Markets deliberately track the live shortlist rather than the loaded one: nothing patches
      // them optimistically, so a remove reloading them is how the location pills drop the removed
      // agent's markets. The compare page has no add path (adds happen via the shortlist button on
      // profile/location pages), so the two inputs can't currently disagree — but an add reaching
      // this store would reload markets while `agents` sat still, and it would need patching here.
      loadMarkets(computed(() => (store.location() ? [] : shortlistService.shortlist())));
    };

    return {
      sort(sortBy: SortByType): void {
        patchState(store, { sortBy });
      },
      setPropertyType(propertyType: string | null): void {
        patchState(store, { propertyType });
      },
      remove(agentCode: string): void {
        patchState(store, (state) => ({
          agents: state.agents.filter((agent) => agent.agentCode !== agentCode),
        }));

        shortlistService
          .remove(agentCode)
          .pipe(
            take(1),
            catchError((error) => {
              console.error('Failed to remove agent:', error);
              return EMPTY;
            }),
          )
          .subscribe();
      },
      init(): void {
        if (shortlistService.location) {
          patchState(store, { location: shortlistService.location });
          loadEffects();
        } else if (platformService.isPlatformBrowser) {
          loadEffects();
        }
      },
    };
  }),
  withHooks({
    onInit(store) {
      store.init();
    },
  }),
);
